import { useCreateOrganizationMutation } from "@/features/organizations/api/organizationsApi";
import { blankableUrl } from "@/features/organizations/lib/blankableUrl";
import { slugFromDomain } from "@/features/organizations/lib/slugFromDomain";
import { handleMutationError } from "@/shared/api/handleMutationError";
import { notify } from "@/shared/components/notify";
import { zodResolver } from "@/shared/forms/zodResolver";
import { CreateOrganizationSchema } from "@keystone/contracts/organizations";
import { ErrorCode } from "@keystone/contracts/shared";
import { Button, Group, Modal, Stack, TextInput } from "@mantine/core";
import { useForm } from "@mantine/form";
import { useRef } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface CreateOrganizationModalProps {
  opened: boolean;
  onClose: () => void;
}

interface FormValues {
  name: string;
  customerDomain: string;
  slug: string;
  logoUrl: string;
}

export function CreateOrganizationModal({ opened, onClose }: CreateOrganizationModalProps) {
  const { t } = useTranslation("organizations");
  const [createOrganization, { isLoading }] = useCreateOrganizationMutation();
  const slugManuallyEditedRef = useRef(false);

  const form = useForm<FormValues>({
    initialValues: { name: "", customerDomain: "", slug: "", logoUrl: "" },
    validate: zodResolver(
      CreateOrganizationSchema.extend({
        customerDomain: z.string().trim().max(255).default(""),
        logoUrl: blankableUrl(t("form.errors.invalidUrl")).default(""),
      }),
    ),
    validateInputOnBlur: true,
  });

  const handleDomainChange = (value: string) => {
    form.setFieldValue("customerDomain", value);
    if (!slugManuallyEditedRef.current) {
      form.setFieldValue("slug", slugFromDomain(value));
    }
  };

  const handleSlugChange = (value: string) => {
    slugManuallyEditedRef.current = value.length > 0;
    form.setFieldValue("slug", value);
  };

  const handleClose = () => {
    form.reset();
    slugManuallyEditedRef.current = false;
    onClose();
  };

  const handleSubmit = form.onSubmit(async (values) => {
    try {
      await createOrganization({
        name: values.name,
        slug: values.slug,
        ...(values.customerDomain ? { customerDomain: values.customerDomain } : {}),
        ...(values.logoUrl ? { logoUrl: values.logoUrl } : {}),
      }).unwrap();
      notify.success("organizations:notifications.created");
      handleClose();
    } catch (e) {
      const code = handleMutationError(e, "organizations:errors.createFailed");
      if (code === ErrorCode.OrganizationSlugTaken) {
        form.setFieldError("slug", t("form.errors.slugTaken"));
      }
    }
  });

  return (
    <Modal opened={opened} onClose={handleClose} title={t("create.title")} centered>
      <form onSubmit={handleSubmit} noValidate>
        <Stack gap="sm">
          <TextInput
            label={t("form.name")}
            placeholder={t("form.namePlaceholder")}
            required
            {...form.getInputProps("name")}
          />
          <TextInput
            label={t("form.customerDomain")}
            placeholder={t("form.customerDomainPlaceholder")}
            value={form.values.customerDomain}
            onChange={(e) => handleDomainChange(e.currentTarget.value)}
            error={form.errors.customerDomain}
            onBlur={() => form.validateField("customerDomain")}
          />
          <TextInput
            label={t("form.slug")}
            placeholder={t("form.slugPlaceholder")}
            required
            description={t("form.slugDescription")}
            value={form.values.slug}
            onChange={(e) => handleSlugChange(e.currentTarget.value)}
            error={form.errors.slug}
            onBlur={() => form.validateField("slug")}
          />
          <TextInput
            label={t("form.logoUrl")}
            placeholder={t("form.logoUrlPlaceholder")}
            {...form.getInputProps("logoUrl")}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" onClick={handleClose} type="button">
              {t("common:actions.cancel")}
            </Button>
            <Button type="submit" loading={isLoading}>
              {t("create.submit")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
